import React, { FC, useEffect } from 'react'
import { Badge, Card, Col, Grid, Row, Space, Tooltip, Typography, Image } from 'antd'
import { formatCurrency, getPlanMultiplier, getPricePeriod, getResourceType, SelectedProduct } from '../utils'
import { usePaymentMethod, useShopping } from '../hooks'
import { StyledInputNumber } from './StyledInputNumber'
import { InfoCircleFilled } from '@ant-design/icons'

const { Text, Title } = Typography

interface SoftwareCardProps {
  productDetails: SelectedProduct
}

export const SoftwareCard: FC<SoftwareCardProps> = ({
  productDetails
}) => {
  const { chart, setChart } = useShopping()
  const { billingPeriod } = usePaymentMethod()
  const screens = Grid.useBreakpoint()
  
  const { product, price, meta } = productDetails
  const unitPrice = parseFloat(price.unit_price.amount)
  const resourceCount = getPlanMultiplier(price)
  const perUnitPrice = resourceCount > 1 ? unitPrice / resourceCount : unitPrice

  useEffect(() => {
    setChart(prev => ({
      ...prev,
      [product.id]: {
        key: product.id,
        product: productDetails,
        quantity: prev[product.id]?.quantity || 0,
        isValid: !productDetails.meta?.hasConflict
      }
    }))
  }, [productDetails, setChart, billingPeriod])

  const card = <Card size={'small'}
    style={{
      backgroundColor: meta?.hasConflict 
        ? '#f5f5f5' 
        : product.id in chart 
        && chart[product.id]?.quantity 
        && chart[product.id]?.quantity > 0 
        ? '#E6F7FF' 
        : undefined,
      opacity: meta?.hasConflict ? 0.6 : 1,
      transition: 'background-color 0.5s ease'
    }}
  >
    <Row align="middle" gutter={[8, 8]}
      title={meta?.hasConflict ? (
      'You already have an active subscription for this product with the selected billing period.'
    ) : undefined}
    >
      <Col xs={24} sm={24} md={12} style={{
        textAlign: 'left',
      }}>
        <Space size={'middle'}>
          <Image src={product.image_url} 
            alt={product.name} 
            style={{ width: meta?.isSeatProduct ? '40px' : '60px' }} 
            preview={false}
          />
          <Text>{product.name}</Text>
          <Tooltip title={product.description}>
            <InfoCircleFilled style={{
              color: '#1890ff'
            }}/>
          </Tooltip>
        </Space>
      </Col>
      <Col xs={24} sm={24} md={12} style={{ textAlign: 'right' }}>
        <Space size={'middle'} direction='vertical'>
          <Space size={'small'}>
            <Title level={5} style={{ margin: 0, color: '#1890ff' }}>
              {formatCurrency(unitPrice, price.unit_price.currency_code)}
            </Title>
            <Text type="secondary" style={{ fontSize: '12px', fontWeight: 'normal' }}>
              / {getPricePeriod(price) === 'yearly' ? 'year' : 'month'}
            </Text>
          </Space>
          {resourceCount > 1 && (
            <Text type="secondary" style={{ fontSize: '12px', fontWeight: 'normal', display: 'block' }}>
              ({formatCurrency(perUnitPrice, price.unit_price.currency_code)} per {getResourceType(product).singular})
            </Text>
          )}
          <StyledInputNumber
            min={0}
            value={product.id in chart 
              ? chart[product.id]?.quantity 
              : 0}
            onChange={(val) => {
              setChart(prev => ({
                ...prev,
                [product.id]: {
                  ...prev[product.id],
                  quantity: val || 0
                }
              }))
            }}
            style={{ width: '100%', maxWidth: 120 }}
            addonBefore={<Typography.Text strong>Qty</Typography.Text>}
            disabled={meta?.hasConflict}
          />
        </Space>
      </Col>
    </Row>
  </Card>

  return (
    card
  )
}
